import type { Avatar } from "@repo/shared/types/avatar";
import type { BaseIconName, CustomIconName } from "@repo/ui/components/icon";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";

interface AvatarDisplayProps {
  /** Avatar value from the database. `null` or `undefined` renders the fallback. */
  avatar?: Avatar | null;
  /** Icon name to show when no avatar is set. Omit to render nothing. */
  fallbackIcon?: BaseIconName;
  /** CSS class applied to the root element. */
  className?: string;
}

/**
 * Renders an avatar value (icon, emoji, or image) with an optional fallback.
 * When no `fallbackIcon` is provided, returns `null` for empty avatars.
 */
export const AvatarDisplay = ({
  avatar,
  fallbackIcon,
  className,
}: AvatarDisplayProps) => {
  if (!avatar) {
    if (!fallbackIcon) return null;
    return (
      <Icon
        name={fallbackIcon}
        className={cn("size-4 text-muted-foreground", className)}
      />
    );
  }

  switch (avatar.type) {
    case "emoji":
      return (
        <span className={cn("text-base leading-none", className)}>
          {avatar.emoji}
        </span>
      );
    case "icon":
      return (
        <Icon.Custom
          name={avatar.icon as CustomIconName}
          className={cn("size-4", className)}
          style={{ color: avatar.color }}
        />
      );
    case "image":
      return (
        <img
          src={avatar.url}
          alt=""
          className={cn("size-4 rounded object-cover", className)}
        />
      );
  }
};
